import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { apiGet, type Campaign } from "../api/client";
import { formatShortDate, formatTimeParts } from "../lib/format";

type CampaignShow = {
  campaign: Campaign;
  config?: {
    schedule?: { job_types?: Record<string, { disp: string; dir: string; trigger_disp: string }> };
    when?: { models?: Record<string, { disp: string; verbs: Record<string, { disp: string }> }> };
  };
};

export function CampaignEditPage() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const [data, setData] = useState<CampaignShow | null>(null);
  const tab = pathname.includes("/settings")
    ? "settings"
    : pathname.includes("/sms")
      ? "sms"
      : pathname.includes("/logs")
        ? "logs"
        : "email";

  useEffect(() => {
    if (!id) return;
    apiGet<CampaignShow>(`/campaigns/${id}`, { config: true }).then(setData);
  }, [id]);

  if (!data?.campaign) {
    return (
      <div className="ne__empty-message">
        <p>Loading campaign…</p>
      </div>
    );
  }
  const campaign = data.campaign;
  const jobTypes = data.config?.schedule?.job_types || {};

  return (
    <div className="ne__campaign__edit-page">
      <div className="ne__page-header ne__page-header--tabs">
        <div className="ne__page-header__header">
          <div className="ne__campaign__edit">
            <h2 className="ne__campaign__name">{campaign.name}</h2>
          </div>
        </div>
        <ul className="ne__tab__group">
          <li>
            <NavLink
              className={({ isActive }) => `ne__tab${isActive ? " active" : ""}`}
              to={`/app/campaigns/${id}/edit/email`}
            >
              Email
            </NavLink>
          </li>
          <li>
            <NavLink
              className={({ isActive }) => `ne__tab${isActive ? " active" : ""}`}
              to={`/app/campaigns/${id}/edit/sms`}
            >
              SMS
            </NavLink>
          </li>
          <li>
            <NavLink
              className={({ isActive }) => `ne__tab${isActive ? " active" : ""}`}
              to={`/app/campaigns/${id}/edit/logs`}
            >
              Logs
            </NavLink>
          </li>
          <li>
            <NavLink
              className={({ isActive }) => `ne__tab${isActive ? " active" : ""}`}
              to={`/app/campaigns/${id}/edit/settings`}
            >
              Settings
            </NavLink>
          </li>
        </ul>
      </div>

      <div className="ne__campaigns__view">
        {tab === "email" ? (
          <div className="ne__campaign__tab ne__campaign__tab--email">
            <header className="ne__campaign__header">
              <h3 className="ne__h3">Email</h3>
            </header>
            <fieldset className="ne__fieldset ne__campaign__subject">
              <label className="ne__fieldset__label">
                <input
                  className="ne__input"
                  type="text"
                  name="subject"
                  defaultValue={campaign.subject || ""}
                  readOnly
                />
                <span className="ne__placeholder">Subject</span>
              </label>
            </fieldset>
            <div
              className="ne__campaign__email"
              dangerouslySetInnerHTML={{
                __html: campaign.content?.email?.body || "<p>No email body</p>",
              }}
            />
          </div>
        ) : null}

        {tab === "sms" ? (
          <div className="ne__campaign__tab ne__campaign__tab--sms">
            <header className="ne__campaign__header">
              <h3 className="ne__h3">SMS</h3>
            </header>
            <textarea
              className="ne__textarea ne__campaign__sms-body"
              rows={5}
              defaultValue={campaign.content?.sms?.body || ""}
              readOnly
            />
            <p className="ne__campaign__sms-count">
              {(campaign.content?.sms?.body || "").length} / 144
            </p>
          </div>
        ) : null}

        {tab === "settings" ? (
          <div className="ne__campaign__tab ne__campaign__tab--settings">
            <header className="ne__campaign__header">
              <h3 className="ne__h3">Settings</h3>
            </header>
            {campaign.misc?.type === "schedule" ? (
              <>
                <fieldset className="ne__campaign__function">
                  <header className="ne__list__header">
                    <h5 className="ne__list__heading">Which function?</h5>
                  </header>
                  <ul className="ne__list__list">
                    {Object.entries(jobTypes).map(([key, value]) => (
                      <li key={key}>
                        <span
                          className={`ne__list__link${
                            campaign.misc?.job_type === key ? " active" : ""
                          }`}
                        >
                          {value.disp}
                        </span>
                      </li>
                    ))}
                  </ul>
                </fieldset>
                <fieldset className="ne__schedule ne__campaign__schedule">
                  <header className="ne__schedule__header">
                    <h5 className="ne__schedule__heading">
                      Campaigns will be sent…
                    </h5>
                  </header>
                  <ul className="ne__schedule__list">
                    {(campaign.misc?.schedule || []).map((sched, i) => {
                      const jt =
                        jobTypes[campaign.misc?.job_type || "reminder"] ||
                        Object.values(jobTypes)[0];
                      return (
                        <li key={i} className="ne__schedule__item">
                          <div className="ne__schedule__description">
                            <strong>{sched.num}</strong>{" "}
                            <strong>
                              {sched.num === 1 ? sched.unit : `${sched.unit}s`}
                            </strong>{" "}
                            <span>
                              {jt?.dir} {jt?.trigger_disp}
                            </span>
                            <ul className="ne__schedule__options">
                              {sched.enable_email ? (
                                <li className="ne__schedule__option">
                                  Email
                                  {sched.has_extra_email_text
                                    ? " with extra text"
                                    : ""}
                                </li>
                              ) : null}
                              {sched.enable_sms ? (
                                <li className="ne__schedule__option">SMS</li>
                              ) : null}
                            </ul>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </fieldset>
              </>
            ) : null}
            {campaign.misc?.type === "manual" ? (
              <div>
                <h5>Select patients manually</h5>
                <p>Filter patients by criteria (optional)</p>
                <button
                  type="button"
                  className="ne__btn ne__btn--secondary ne__btn--lg"
                >
                  Update campaign recipients
                </button>
              </div>
            ) : null}
            {campaign.misc?.type === "when" ? (
              <div>
                <h5>Send this campaign when…</h5>
                <p>
                  Model: {campaign.misc.model} · Verb: {campaign.misc.verb}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === "logs" ? (
          <CampaignLogs id={id!} jobType={campaign.misc?.job_type} />
        ) : null}
      </div>
    </div>
  );
}

type CampaignLogRow = {
  id: string;
  context_id?: string;
  reminder?: string;
  channel?: string;
  status?: string;
  body_preview?: string;
  created_at?: string;
  confirmed?: boolean;
  patient_name?: string;
  recipient?: {
    name?: string;
    bio?: { date_of_birth?: string; phone_number?: string };
  };
  owner?: { doctor_name?: string };
  appointment?: { id?: string; start_time?: string };
};

function jobTypeLabel(raw?: string | null): string {
  if (!raw) return "";
  return raw.replace(/_/g, " ");
}

function CampaignLogs({
  id,
  jobType,
}: {
  id: string;
  jobType?: string;
}) {
  const [logs, setLogs] = useState<CampaignLogRow[]>([]);
  const [query, setQuery] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    apiGet<{ logs?: CampaignLogRow[] }>(`/campaigns/${id}/logs`).then((d) => {
      if (cancelled) return;
      setLogs(d?.logs ?? []);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((log) => {
      const name = log.recipient?.name || log.patient_name || "";
      return name.toLowerCase().includes(q);
    });
  }, [logs, query]);

  const showConfirmed = logs.some((l) => typeof l.confirmed === "boolean");

  return (
    <div className="ne__campaign__tab ne__campaign__tab--logs">
      <div className="ne__table__filters">
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <fieldset className="ne__search ne__search--expandable">
            <label>
              <i className="ne__icon__search" aria-hidden />
              <input
                className="ne__search__input"
                type="search"
                placeholder="Search by recipient name"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <span className="ne__search__border" />
            </label>
          </fieldset>
        </form>
      </div>

      {loaded && filtered.length === 0 ? (
        <div className="ne__empty-message">
          <h2>Sorry, no matching logs!</h2>
        </div>
      ) : (
        <div className="ne__table__padding">
          <table className="ne__table ne__table--icon">
            <thead>
              <tr>
                <th />
                <th className="ne__table__eyebrow">Patient</th>
                <th className="ne__table__eyebrow">Communication</th>
                <th className="ne__table__eyebrow">Type</th>
                <th className="ne__table__eyebrow">Sent</th>
                <th className="ne__table__eyebrow">Provider</th>
                <th className="ne__table__eyebrow">Appointment Date</th>
                {showConfirmed ? (
                  <th className="ne__table__eyebrow ne__table__center">
                    User action
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {filtered.map((log) => {
                const sent = formatTimeParts(log.created_at);
                const appt = formatTimeParts(log.appointment?.start_time);
                const channel = (log.channel || "sms").toLowerCase();
                const patientName =
                  log.recipient?.name || log.patient_name || "Patient";
                return (
                  <tr key={log.id}>
                    <td>
                      <i
                        className="ne__icon__megaphone--thinnest"
                        aria-hidden
                      />
                    </td>
                    <td>
                      <h3 className="ne__activity-log__patient">
                        {patientName}
                      </h3>
                      {log.recipient?.bio?.date_of_birth ? (
                        <span className="ne__activity-log__dob">
                          {formatShortDate(log.recipient.bio.date_of_birth)}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <span className="ne__activity-log__comm-type">
                        {[log.reminder, jobTypeLabel(jobType)]
                          .filter(Boolean)
                          .join(" ")}
                      </span>
                    </td>
                    <td>
                      <span
                        className="ne__activity-log__contact-type"
                        title={
                          log.recipient?.bio?.phone_number ||
                          log.body_preview ||
                          ""
                        }
                      >
                        {channel === "email" ? "Email" : "SMS"}
                      </span>
                    </td>
                    <td>
                      <span className="ne__activity-log__sent-day">
                        {formatShortDate(log.created_at)}
                      </span>{" "}
                      <span className="ne__activity-log__sent-time">
                        {sent.time}
                        <span className="ne__period">{sent.period}</span>
                      </span>
                    </td>
                    <td>
                      <span className="ne__activity-log__provider">
                        {log.owner?.doctor_name || "—"}
                      </span>
                    </td>
                    <td>
                      {log.appointment?.start_time ? (
                        <>
                          <span className="ne__activity-log__appt-day">
                            {formatShortDate(log.appointment.start_time)}
                          </span>{" "}
                          <span className="ne__activity-log__appt-time">
                            {appt.time}
                            <span className="ne__period">{appt.period}</span>
                          </span>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    {showConfirmed ? (
                      <td className="ne__table__center">
                        {log.confirmed ? (
                          <div className="ne__table__led">
                            <i
                              className="ne__icon__dot ne__activity-log__dot--seen"
                              title="Patient confirmed their appointment"
                              aria-hidden
                            />
                            <span>Confirmed</span>
                          </div>
                        ) : (
                          <div className="ne__table__led">
                            <i
                              className="ne__icon__dot ne__activity-log__dot--not-seen"
                              title="The appointment is unconfirmed"
                              aria-hidden
                            />
                            <span>Not Confirmed</span>
                          </div>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
